
import { t } from "../lib/i18n";
/**
 * 「なぜこの数量か」（L4根拠）の説明文を組み立てる純関数。
 *
 * 推奨発注数量は `補充目標 S − 在庫(available) − 入荷予定(incoming)` で決まる。
 * S は (s, S) 方策の補充目標で、`補充日数` が未設定なら S = 発注点 s に縮退する
 * （＝従来どおり発注点ちょうどまで補充）。その場合は用語を増やしても情報が
 * 増えないので「発注点」表記のままにする。
 *
 * 既発注ぶん(incoming)を引くことで二重発注を防いでいることも明示する。
 * 予測由来の値（発注点・補充目標・推奨数量）が確定している自動注文書の明細だけに
 * 適用する。手動注文書ではこれらが null なので呼び出し側でガードする。
 */
interface ReasonLine {
  reorder_point: number;
  /** 補充目標 S。手動注文書やデプロイスキュー中の旧 API では null。 */
  replenishment_target: number | null;
  available: number;
  incoming: number;
  /**
   * アプリ内で「注文済みとしてマーク」した未入荷分。backend は incoming と
   * 同列に数量から差し引くので、0 でないときは項として出さないと引き算が
   * 合わない説明になる。旧 API では欠けるため optional。
   */
  ordered_open?: number;
  recommended_qty: number;
  /**
   * 数量を引き上げた MOQ（効いた行のみ・旧 API では欠ける）。
   * 🔴 これを項に出さないと、MOQ で跳ねた行＝いちばん説明の要る行で
   * 引き算の右辺と表示数量が一致しない（画面の算数が壊れて見える）。
   */
  applied_moq?: number | null;
  /** 数量を切り上げた入数（効いた行のみ）。同上。 */
  applied_pack_size?: number | null;
}

function hasReplenishTarget(
  line: Pick<ReasonLine, "reorder_point" | "replenishment_target">,
): line is Pick<ReasonLine, "reorder_point"> & { replenishment_target: number } {
  return (
    line.replenishment_target !== null &&
    line.replenishment_target > line.reorder_point
  );
}

export function purchaseOrderReason(line: ReasonLine): string {
  const target = hasReplenishTarget(line)
    ? t("補充目標 {n}", { n: line.replenishment_target })
    : t("発注点 {n}", { n: line.reorder_point });
  const orderedOpen = line.ordered_open ?? 0;
  const orderedOpenTerm =
    orderedOpen > 0 ? t(" − 発注済み（未入荷） {n}", { n: orderedOpen }) : "";
  const hasRounding =
    line.applied_moq != null || line.applied_pack_size != null;
  if (!hasRounding) {
    return t("{target} − 在庫 {available} − 入荷予定 {incoming}{ordered} = {qty}", {
      target,
      available: line.available,
      incoming: line.incoming,
      ordered: orderedOpenTerm,
      qty: line.recommended_qty,
    });
  }
  // 丸めが効いた行＝生の必要数と最終数量を分けて出す（引き算の右辺を最終数量に
  // すると算数が合わない）。生の必要数は backend が返さないので S 側から復元する。
  const targetValue = hasReplenishTarget(line)
    ? line.replenishment_target
    : line.reorder_point;
  const rawQty =
    targetValue - line.available - line.incoming - orderedOpen;
  const moqTerm =
    line.applied_moq != null
      ? t(" → 最低発注数量（MOQ）{n} に引き上げ", { n: line.applied_moq })
      : "";
  const packTerm =
    line.applied_pack_size != null
      ? t(" → 入数 {n} の倍数に切り上げ", { n: line.applied_pack_size })
      : "";
  return t(
    "{target} − 在庫 {available} − 入荷予定 {incoming}{ordered} = {raw}{moq}{pack} = {qty}",
    {
      target,
      available: line.available,
      incoming: line.incoming,
      ordered: orderedOpenTerm,
      raw: rawQty,
      moq: moqTerm,
      pack: packTerm,
      qty: line.recommended_qty,
    },
  );
}

/**
 * 補充目標の内訳（発注点 + 補充日数ぶんの想定販売数）。
 * 補充日数が未設定で目標＝発注点のときは説明不要なので null を返す。
 */
export function replenishmentTargetBreakdown(
  line: Pick<ReasonLine, "reorder_point" | "replenishment_target">,
): string | null {
  if (!hasReplenishTarget(line)) return null;
  const uplift = line.replenishment_target - line.reorder_point;
  return t("補充目標 {target} = 発注点 {point} + {uplift}（補充日数ぶんの想定販売数）", {
    target: line.replenishment_target,
    point: line.reorder_point,
    uplift,
  });
}
